import { messageOf } from '../lib/errors'
import { dateLocaleFor, useHubLang } from '../lib/i18n'
import { WIKI, HOME } from '../lib/hubCopy'
import { myLanes } from '../lib/support'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { Me } from '../lib/me'
import { can, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { supabase } from '../lib/supabase'
import { AppShell } from '../components/AppShell'
import { AskOnePanel } from '../components/wiki/AskOnePanel'
import { Card } from '../components/Card'
import { VoteButton } from '../components/wiki/VoteButton'
import { Thread } from '../components/Thread'
import { ConeTecMark } from '../components/icons'
import { RowToggle, StillTrue } from '../components/wiki/RowToggle'
import { EditAnswerDialog } from '../components/wiki/EditAnswerDialog'
import { TemperaturePill } from '../components/wiki/TemperaturePill'
import { TranslatedLine, useOriginal } from '../components/TranslatedLine'
import { GuideLink } from '../components/guides/GuideLink'
import {
  askOne,
  wikiByIds,
  searchWiki,
  WIKI_LIST_LIMIT,
  loadQuestionQueue,
  answerQuestion,
  declineQuestion,
  curateAnswer,
  curateQuestion,
  publishWikiContent,
  unpublishWikiContent,
  loadWikiOriginal,
  deleteWikiContent,
  type WikiResult,
  type WikiQuestion,
  type WikiSort,
  type WikiFilters,
  type OneAnswer,
  TEMPERATURES,
} from '../lib/wiki'

/** What a reader sees. Curator mode starts from exactly this and changes it
 *  deliberately, so "turn it off" always lands back on the employee view. */
const READER_VIEW: WikiFilters = {
  state: 'published',
  official: 'any',
  review: 'any',
  source: 'any',
  owner: 'any',
  temperature: 'any',
}

/**
 * Wiki One — the app's fourth route.
 *
 * ── LAYOUT ──────────────────────────────────────────────────────────────────
 *
 * Two columns, reusing .hub__cols / .hub__main / .hub__side rather than a
 * parallel layout of its own. That is not tidiness: .hub__side carries SHL-6's
 * fix, where a sticky rail taller than the viewport left its own bottom
 * permanently below the fold. A second sticky column defined here would have
 * had to rediscover that.
 *
 * ── THE ORDER OF THE LIST IS NOT THIS FILE'S BUSINESS ───────────────────────
 *
 * wiki_search() returns rows in the order they must appear: official first,
 * before relevance is consulted, then relevance, then votes. Sorting or
 * grouping `results` here would silently disagree with the database, and the
 * disagreement would be invisible until somebody marked something official and
 * it did not move.
 *
 * ── STATUS IS A PILL, NOT A COLOURED WORD ───────────────────────────────────
 *
 * Official, Question, Unpublished and stale each get a bordered pill so a
 * reader scanning forty rows gets the state from shape before they get it from
 * reading. Metadata is set in the mono face and the answer is not: owner,
 * review date and counts are values you look up, the answer is prose you read.
 *
 * ── IT LOST ITS ASK BOX ON 17 SEP AND GOT IT BACK ON 22 SEP ────────────────
 *
 * WIK-19 took Ask One off this screen when the drawer arrived, on the argument
 * that a second Ask One surface is a second place to keep the AI disclosure,
 * the rating, the routing and the public-name warning correct. What replaced
 * it was one teal button calling `useAskOne()` from THIS function's body —
 * above the provider AppShell renders — so it got the default no-op and did
 * nothing for five days while the suite asserted it called the hook.
 *
 * David, 22 Sep: "go back to Ask One first and then the path to post the
 * question. Ask One gives the AI summary and also surfaces the related
 * questions. Keep the search FAQ." So the box asks again, ON THE PAGE, and the
 * argument above is satisfied a different way: `AskOnePanel` renders the
 * drawer's own `OneMessage`, `Thumbs`, `RouteStep` and `RouteSent`, and the
 * send is the drawer's `useRouting`. One implementation, two places. Nothing
 * in this file may grow its own copy of any of those — the suite reads this
 * source for `askQuestion(`, `postQuestion(` and the warning's wording.
 *
 * SEARCHING is looking for an answer that exists; ASKING is admitting one
 * might not. They share the box and not the button: Ask One is the submit,
 * Search FAQ is beside it.
 *
 * ── WHERE 034'S WARNING WENT ────────────────────────────────────────────────
 *
 * This hub told everybody "Questions are asked without a name attached" until
 * 8 Sep. 032 recorded the asker and that line came down; 034 put the question
 * itself in front of ~900 people. The ask box stated all of that before
 * anybody typed, and THAT is what made a named public question defensible.
 *
 * A drawer breaks the timing — the person has already typed, conversationally
 * and in private — so the whole disclosure moved with the act, to the moment
 * of choosing, where they see and can edit the exact text that will publish.
 * It is in AskOneDrawer.tsx's RouteStep. Nothing on this screen may quietly
 * grow a shortcut that posts a question without it.
 *
 * ── CURATOR MODE IS A SWITCH, NOT A PERMISSION (David, 8 Sep) ───────────────
 *
 * A wiki.curate holder gets a toggle. OFF is byte-for-byte the employee view —
 * published only, no filters, no state pills. ON adds the filter bar and the
 * unpublished content.
 *
 * The toggle exists rather than the controls simply being always-on, and the
 * reason is the 26 unapproved Brand Change FAQ drafts (seed 005, WIK-9). With
 * always-on curation there is no way for Chandra to see what ~900 people see:
 * her default board would carry 26 rows nobody else has, permanently, and the
 * question "does the board look right yet" would have no answer she could
 * check. Entering curator mode is one deliberate act; leaving it returns to
 * the reader's view exactly.
 *
 * ── AND OFF MEANS OFF FOR EVERY CONTROL, NOT ONLY THE FILTERS (David, 23 Sep)
 *
 * Until 23 Sep the switch governed the filter bar and the unpublished rows,
 * and nothing else: a curator browsing the published board still got
 * Draft/Published, Unofficial/Official, the confirm button and Remove on every
 * row, plus three side cards ("Questions waiting", "What people asked and
 * didn't get", "Wiki One at a glance"). So "byte-for-byte the employee view"
 * above was false for exactly the people it was written about. Now:
 *
 *   - `curatorOn` gates every row control and all three cards. The glance card
 *     goes with them, for EVERYBODY — its figures are curator figures, and its
 *     "Open questions" read 0 for every employee because only curators load
 *     the queue. With the rail empty the page is one column.
 *   - `ownerControls` gates the two powers an ordinary OWNER has (031b's
 *     confirm-your-own-answer, 041's delete-your-own-draft). For somebody WITH
 *     curator mode they wait for the mode too — David owns most of the FAQ, and
 *     leaving those on would put a confirm button on nearly every row of his
 *     "employee" view. For somebody WITHOUT the mode there is nothing to enter,
 *     so they stay exactly as they were.
 *   - The content check's temperature (098) is curator-mode only: a pill on
 *     every row and a filter row in the bar.
 *
 * The mode is OFFERED to wiki.curate OR content.publish (B18 keeps them apart
 * in the database; they travel together on comms_admin today), so nobody
 * loses the publish toggle by holding one without the other.
 *
 * **It is not a security boundary and nothing may treat it as one.** Every
 * filter it exposes is a request, RLS decides, and an employee who called the
 * RPC by hand with state => 'unpublished' would get their own drafts — which
 * is exactly what they already see on their own question. Hiding the controls
 * is about not putting a curator's tools in a driller's way.
 */
export function Wiki({
  me,
  initialQuery = '',
  initialOpen,
}: {
  me: Me
  initialQuery?: string
  /** 125 (HLP-23). One item a bell row pointed at, opened above the board. */
  initialOpen?: string
}) {
  const lang = useHubLang()
  const w = WIKI[lang]
  /** One sentence, two places — the hover title and the inline line a tap
   *  shows. Its words are lib/hubCopy.ts's `postHint` (SHL-25). */
  const POST_HINT = w.postHint
  /* Seeded from #/wiki?q=... so a link from the Brand Change FAQ notice lands
     on the answers rather than on an empty box. See App.tsx for why a query
     string is not the router trigger. It is an INITIAL value, not a controlled
     one: once the person is here the box is theirs, and re-imposing the URL's
     query on every render would fight anybody who typed something else. */
  const [query, setQuery] = useState(initialQuery)
  const [results, setResults] = useState<WikiResult[] | null>(null)
  const [curating, setCurating] = useState(false)
  const [filters, setFilters] = useState<WikiFilters>(READER_VIEW)
  const [sort, setSort] = useState<WikiSort>('best')
  const [openThread, setOpenThread] = useState<string | null>(initialOpen ?? null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const [queue, setQueue] = useState<WikiQuestion[] | null>(null)

  /* ── ASK ONE IS BACK ON THIS SCREEN (David, 22 Sep) ─────────────────────
     The box asks One first — the summary, then the related questions under
     it, then the way to a person — and "Search FAQ" beside it is the plain
     lookup. WHAT the list below is showing is `listMode`, and it is said in a
     heading over the list, because the same list silently changing meaning
     is what made search look like it did nothing.

     `askSeq` keys the panel so every new question remounts it: the routing
     state (which door, what was typed) belongs to ONE question. */
  const [asked, setAsked] = useState<{ q: string; answer: OneAnswer | null } | null>(null)
  const [asking, setAsking] = useState(false)
  const [askSeq, setAskSeq] = useState(0)
  // 'opened' (125): the list is the ONE item a notification pointed at, with
  // its thread open. "Back to the board" leaves it like any search.
  const [listMode, setListMode] = useState<'board' | 'search' | 'ask' | 'opened'>(
    initialOpen ? 'opened' : initialQuery ? 'search' : 'board',
  )

  /* ── POST TO THE BOARD IS ALWAYS ON SCREEN, AND ASKS ONE FIRST (David, 23 Sep)
     It sat behind a thumbs-down or a "no answer", so a reader with a real
     question and a decent-looking answer had no visible way to put it to
     people. Now it is a button in the row, greyed until One has read THIS
     question — the text in the box and the question One answered must match,
     or the check was of a different question.

     Greyed with `aria-disabled`, never `disabled`: a disabled button takes no
     focus and, in some browsers, no pointer events, so the reason it is grey
     could only be learned by already knowing it. The hover `title` is the
     desktop half; pressing it anyway shows the same sentence inline, because
     there is no hover on a tablet (046, RoleRow.tsx).

     It SENDS NOTHING. `postSeq` asks the panel to open its board route; the
     warning, the editable text and the write all stay in RouteStep and
     `useRouting`, and the suite reads this file to keep it that way. */
  const [postSeq, setPostSeq] = useState(0)
  const [postHint, setPostHint] = useState(false)
  const canPost = !!asked && !!asked.answer && !asking && query.trim() === asked.q


  const canCurate = can(me, 'wiki.curate')
  // B18: publishing is content.publish, not wiki.curate. They travel together
  // on comms_admin today, but the database checks them separately and this
  // must not collapse into canCurate.
  const canPublish = can(me, 'content.publish')
  /* 23 Sep. See the header: OFF is the employee's view for every control and
     card, and the owner powers wait for the mode for anybody who HAS one. */
  const curatorOffered = canCurate || canPublish
  const curatorOn = curatorOffered && curating
  const ownerControls = !curatorOffered || curating

  /**
   * Every read goes through here with the CURRENT filters unless a caller
   * passes new ones explicitly.
   *
   * The explicit-override argument exists because React state is not applied
   * until the next render: a control that calls setFilters and then runSearch
   * would search with the PREVIOUS filter set and show a list that disagrees
   * with the controls above it — right on the next keystroke, wrong now, and
   * indistinguishable from a caching bug.
   */
  async function runSearch(
    q: string,
    f: WikiFilters = filters,
    order: WikiSort = sort,
  ) {
    try {
      setResults(await searchWiki(q, { ...f, limit: WIKI_LIST_LIMIT, sort: order }))
      setError(null)
    } catch (e) {
      setError(messageOf(e))
    }
  }

  /**
   * Ask One, on the page. One reads both corpora and answers; the list under
   * the box becomes the RELATED questions — what One cited first, in the order
   * it cited them, then the closest lexical matches it did not cite. Cited
   * first because those are the entries the summary above is made of; a
   * reader checking One's answer should find its sources at the top.
   *
   * `askOne` never throws — One being unavailable comes back as a message —
   * and the lexical half is caught separately, so a failure in either leaves
   * the other on screen.
   */
  async function ask(text?: string) {
    const q = (text ?? query).trim()
    if (!q || asking) return
    setAsking(true)
    setAskSeq((n) => n + 1)
    setPostHint(false)
    setAsked({ q, answer: null })
    setListMode('ask')
    setOpenThread(null)
    setError(null)
    try {
      const [answer, lexical] = await Promise.all([
        askOne(q, { locale: me.locale }),
        searchWiki(q, { ...filters, limit: 10, sort: 'best' }).catch(() => [] as WikiResult[]),
      ])
      const cited = await wikiByIds(answer.used).catch(() => [] as WikiResult[])
      const seen = new Set(cited.map((r) => r.contentId))
      setResults([...cited, ...lexical.filter((r) => !seen.has(r.contentId))])
      setAsked({ q, answer })
    } finally {
      setAsking(false)
    }
  }

  /** Search FAQ — the lookup, with no model in it. Clears One's answer so the
   *  list is not read as "related to" a question nobody asked. */
  function searchOnly() {
    setAsked(null)
    setListMode(query.trim() ? 'search' : 'board')
    void runSearch(query, filters, sort)
  }

  /** Change one filter and re-read with it, in one act. */
  function setFilter<K extends keyof WikiFilters>(key: K, value: WikiFilters[K]) {
    const next = { ...filters, [key]: value }
    setFilters(next)
    void runSearch(query, next)
  }

  /**
   * Entering curator mode opens on the work: unpublished only. That is the
   * pile WIK-9 has to get through, and landing on the published board would
   * make the toggle feel like it did nothing.
   *
   * Leaving RESETS to READER_VIEW rather than remembering what was set. A
   * curator who leaves the mode is asking what everybody else sees, and
   * restoring their last filters would answer a different question.
   */
  function toggleCurating(on: boolean) {
    setCurating(on)
    const next: WikiFilters = on ? { ...READER_VIEW, state: 'unpublished' } : READER_VIEW
    setFilters(next)
    void runSearch(query, next)
  }

  /** Back to the board: drop the query. Filters survive — this clears the
   *  SEARCH, not the view somebody set up. */
  function clearAll() {
    setQuery('')
    setOpenThread(null)
    setAsked(null)
    setListMode('board')
    void runSearch('', filters)
  }

  // An empty query browses rather than returning nothing: wiki_search treats a
  // null tsquery as "no filter", and with every relevance equal it is votes
  // that order the list. The screen therefore opens on the most-upvoted items,
  // which is the browse behaviour, out of the same ORDER BY that does search.
  useEffect(() => {
    let live = true
    // Opens on the reader's view for everybody, curators included. See the
    // header: curator mode is entered deliberately, never by default.
    //
    // An incoming ?q= runs as a real search, through the same function the box
    // uses. Not a filter applied to a browse list: the ranking rule (036, 047)
    // is the ORDER BY, so a "search" that post-filtered results would order by
    // votes rather than by what was asked for.
    // 125. A bell row's `?open=` reads that one item by id — wiki_by_ids, the
    // same read One's citations use — rather than hoping a search finds it.
    ;(initialOpen
      ? wikiByIds([initialOpen])
      : searchWiki(initialQuery, { ...READER_VIEW, limit: WIKI_LIST_LIMIT, sort: 'best' }))
      .then((r) => live && setResults(r))
      .catch((e: Error) => live && setError(e.message))
    return () => {
      live = false
    }
    // initialQuery only matters on mount — it seeds the box above. Re-running
    // on a change would overwrite what the reader has typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Loaded when the mode is ENTERED, not on arrival: the three cards render
  // only in curator mode, and a curator reading the board as everybody sees it
  // should not pay for queue and signal reads nobody is looking at.
  useEffect(() => {
    if (!canCurate || !curating) return
    let live = true
    loadQuestionQueue('open')
      .then((q) => live && setQueue(q))
      .catch(() => live && setQueue([]))
    return () => {
      live = false
    }
  }, [canCurate, curating])

  async function act(id: string, fn: () => Promise<unknown>) {
    setBusy(id)
    setError(null)
    try {
      await fn()
      // Re-read rather than splicing. Every one of these is an UPDATE under
      // RLS and 028c is the worked example of a zero-row UPDATE returning
      // success — a spliced list would show a change that never happened.
      //
      // A consequence worth expecting rather than "fixing": in curator mode
      // filtered to unpublished, publishing a row makes it LEAVE the list.
      // That is the list being honest about what it is showing.
      await runSearch(query, filters)
      if (canCurate && curating) setQueue(await loadQuestionQueue('open'))
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  const answers = results?.filter((r) => r.source === 'wiki_answer') ?? []
  const official = answers.filter((r) => r.isOfficial).length
  const published = answers.filter((r) => r.state === 'published').length
  const unreviewed = answers.filter((r) => r.lastReviewedAt === null).length

  return (
    <AppShell
      current="Wiki One"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
      /* One mode, and it is in the same menu as the hub's three. Before this
         the switch sat in the filter row on this screen and the hub's sat in
         the strip on that one — the same act in two places, which is the
         arrangement ModeSwitch itself was created to end. Empty for anybody
         without wiki.curate, so the menu does not render for them at all. */
      modes={
        curatorOffered
          ? [
              {
                key: 'curator',
                label: 'Curator mode',
                tone: 'work' as const,
                on: curating,
                detail:
                  'Show unpublished content, the row controls, the check’s temperature and the curator cards. Leaving resets to the reader’s view.',
                onToggle: toggleCurating,
              },
            ]
          : []
      }
    >
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      <div className={curatorOn ? 'hub__cols' : 'hub__cols hub__cols--single'}>
        <div className="hub__main">
          <Card title="Wiki One" className="card--wiki">
            {/* ONE BOX, TWO VERBS (22 Sep). Enter asks One — that is the
                default David asked for, "Ask One first" — and Search FAQ is the
                plain lookup beside it. Searching is looking for an answer that
                exists; asking is admitting one might not. They share the box
                and not the button. */}
            <form
              className="wiki-search"
              onSubmit={(e) => {
                e.preventDefault()
                void ask()
              }}
            >
              <label className="sr-only" htmlFor="wiki-q">
                {w.searchLabel}
              </label>
              <input
                id="wiki-q"
                className="wiki-search__input"
                type="search"
                placeholder={w.searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />

              {/* ONE SIZE FOR THE ROW (23 Sep). These were three: `.btn--ai`
                  at 32px in the button face, a bare `.btn` at 40px with no
                  border, and `.btn--quiet` at 32px in the label face. Every
                  control here now carries `btn--sm`, which sets height,
                  padding and face together — the variants differ in colour
                  only. */}
              <div className="wiki-search__actions">
                {/* Teal + the mark is the AI disclosure, the same pairing as the
                    topbar button and the bubble — a disclosure on one surface
                    and not another is not a disclosure. It is the SUBMIT button
                    because Enter should do the thing the screen leads with. */}
                <button
                  className="btn btn--ai btn--sm"
                  type="submit"
                  disabled={asking || !query.trim()}
                >
                  <ConeTecMark size={16} />
                  Ask One
                </button>

                <button
                  className="btn btn--quiet btn--sm"
                  type="button"
                  aria-disabled={!canPost}
                  aria-describedby={canPost ? undefined : 'wiki-post-hint'}
                  title={canPost ? undefined : POST_HINT}
                  onClick={() => {
                    if (!canPost) {
                      setPostHint(true)
                      return
                    }
                    setPostHint(false)
                    setPostSeq((n) => n + 1)
                  }}
                >
                  {w.postToBoard}
                </button>

                <button className="btn btn--quiet btn--sm" type="button" onClick={searchOnly}>
                  {w.searchFaq}
                </button>

                {(query || asked || listMode === 'opened') && (
                  <button className="btn btn--quiet btn--sm" type="button" onClick={clearAll}>
                    {w.backToBoard}
                  </button>
                )}
              </div>
              {/* Always in the DOM so aria-describedby points at something;
                  visible only once somebody has pressed the grey button. */}
              <p
                id="wiki-post-hint"
                className="wiki-search__hint"
                hidden={!postHint || canPost}
                aria-live="polite"
              >
                {POST_HINT}
              </p>
            </form>

            {asked && (
              <AskOnePanel
                key={askSeq}
                question={asked.q}
                answer={asked.answer}
                asking={asking}
                postRequest={postSeq}
                onPosted={() => void runSearch(asked.q, filters, sort)}
                /* v9: a clarifying option is asked as the new question, and
                   lands in the box so the page says what it searched for. */
                onAsk={(q) => { setQuery(q); void ask(q) }}
                onEdit={(q) => setQuery(q)}
              />
            )}

            <div className="wiki-controls">
              {/* `best` is 036's rule. The other three are the reader's own
                  choice and do what they say — a sort that does not sort is
                  worse than no sort. */}
              {/* `.chip` is the app's one filter pill — the same control
                  the feed's audience filter uses. This screen shipped its own
                  `.segmented` on 8 Sep and that was the mistake: two designs
                  for one act. Do not invent a third. */}
              <div className="chips" role="group" aria-label={w.sortLabel}>
                {(
                  [
                    ['best', w.sorts.best],
                    ['trending', w.sorts.trending],
                    ['new', w.sorts.new],
                    ['mine', w.sorts.mine],
                  ] as [WikiSort, string][]
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={`chip chip--sm${sort === key ? ' chip--on' : ''}`}
                    aria-pressed={sort === key}
                    onClick={() => {
                      setSort(key)
                      void runSearch(query, filters, key)
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Curator mode's SWITCH moved to the topbar Options menu on
                  12 Sep, with the hub's other three. It is still the same
                  <ModeSwitch> and still tone `work` — see the `modes` list
                  passed to AppShell above. The BAR below is unchanged and
                  stays here: 039's argument is that you curate the board while
                  looking at the board, which is about where the CONTROLS are,
                  not where the switch is. */}
            </div>

            {/* 353 (HLP-50): beside the curator controls only, never for a reader. */}
            {curatorOn && <GuideLink guide="comms-admin" section="wiki" label="How looking after Wiki One works" />}
            {curatorOn && (
              <CuratorBar
                filters={filters}
                onChange={setFilter}
                onReset={() => {
                  const next: WikiFilters = { ...READER_VIEW, state: 'unpublished' }
                  setFilters(next)
                  void runSearch(query, next)
                }}
                shown={results?.length ?? 0}
              />
            )}


            {/* WHAT ONE READ IS BACK, AS "RELATED QUESTIONS" (22 Sep). After an
                ask, this list is One's citations first and the nearest lexical
                matches after — the entries the summary above was made from,
                where a reader checking it will look. After Search FAQ it is the
                matches; with nothing typed it is the board. The heading says
                which, every time. */}
            {results !== null && listMode !== 'board' && (
              <p className="wiki-results__head">
                {listMode === 'ask'
                  ? w.relatedQuestions
                  : listMode === 'opened'
                    ? w.fromNotification
                    : w.matchesInWiki}
              </p>
            )}
            {results === null || (listMode === 'ask' && asking) ? (
              <p className="empty">{w.loading}</p>
            ) : results.length === 0 ? (
              <p className="empty">
                {listMode === 'opened'
                  ? HOME[lang].itemGone
                  : listMode === 'ask'
                  ? w.nothingClose
                  : query
                    ? w.nothingMatchesWording
                    : w.nothingMatches}
              </p>
            ) : (
              <ul className="wiki-results">
                {results
                  .map((r) => (
                    <ResultRow
                      key={r.contentId}
                      r={r}
                      me={me}
                      busy={busy}
                      openThread={openThread}
                      setOpenThread={setOpenThread}
                      canCurate={canCurate && curatorOn}
                      canPublish={canPublish && curatorOn}
                      ownerControls={ownerControls}
                      showTemperature={curatorOn}
                      onVote={(votes, iVoted) =>
                        setResults((cur) =>
                          (cur ?? []).map((x) =>
                            x.contentId === r.contentId ? { ...x, votes, iVoted } : x,
                          ),
                        )
                      }
                      act={act}
                    />
                ))}
              </ul>
            )}
            {/* REC-54: a limited read says it is a page. wiki_search returns no
                total, so a full page is the only signal there may be more. */}
            {results !== null && listMode !== 'ask' && results.length >= WIKI_LIST_LIMIT && (
              <p className="wiki-results__head">{w.listCapped(WIKI_LIST_LIMIT)}</p>
            )}
          </Card>

        </div>

        {/* THE RAIL IS CURATOR MODE'S (23 Sep). All three cards are a curator's
            instruments, so outside the mode there is no rail at all rather
            than an empty 340px column. */}
        {curatorOn && (
        <aside className="hub__side">
          {canCurate && (
            <Card
              className="wiki-queue-card"
              title="Questions waiting"
              action={<span className="pill">{queue?.length ?? 0}</span>}
            >
              {queue === null ? (
                <p className="empty">Loading…</p>
              ) : queue.length === 0 ? (
                <p className="empty">Nothing waiting.</p>
              ) : (
                <ul className="wiki-queue">
                  {queue.map((q) => (
                    <QueueRow
                      key={q.questionId}
                      q={q}
                      canPublish={canPublish}
                      onDone={async () => {
                        setQueue(await loadQuestionQueue('open'))
                        await runSearch(query, filters)
                      }}
                      onError={setError}
                    />
                  ))}
                </ul>
              )}
            </Card>
          )}

          {/* 126. The Signals card MOVED to #/support/ask-one — one reader of
              one log. This line is the way there, for the curator who used to
              read it here. */}
          {canCurate && (
            <Card title="What people asked One">
              <p className="glance__note">
                What One could and could not answer, the ratings and what to do about them are on{' '}
                <Link to="/support/ask-one">Support → Ask One</Link>.
              </p>
            </Card>
          )}

          <Card title="Wiki One at a glance">
            <div className="glance">
              <p className="glance__row">
                <span className="glance__k">Official answers</span>
                <span className="glance__v">{official}</span>
              </p>
              <p className="glance__row">
                <span className="glance__k">Published</span>
                <span className="glance__v">{published}</span>
              </p>
              <p className="glance__row">
                <span className="glance__k">Never reviewed</span>
                <span className="glance__v">{unreviewed}</span>
              </p>
              <p className="glance__row">
                <span className="glance__k">Open questions</span>
                <span className="glance__v">{queue?.length ?? 0}</span>
              </p>
            </div>
            {/* Counted from what this viewer can see, so it is a reader's view
                of the corpus rather than a claim about the whole tenant. A
                tenant-wide figure would need its own aggregate function and
                would say something different. */}
            <p className="glance__note">Counted from what you can see.</p>
          </Card>
        </aside>
        )}
      </div>
    </AppShell>
  )
}

/**
 * The curator's filter bar. Four axes plus the state, each a small segmented
 * control rather than a dropdown.
 *
 * ── WHY SEGMENTS AND NOT SELECTS ────────────────────────────────────────────
 *
 * Every one of these has two or three options and every option is a word. A
 * <select> hides the current value behind a click and hides the alternatives
 * entirely, so a curator cannot see at a glance that they are looking at
 * "unpublished, unofficial, needs review" — they see three collapsed boxes and
 * have to open each one to find out why the list is short. The commonest
 * confusion with a filtered list is not knowing it is filtered.
 *
 * ── THE COUNT IS PART OF THE CONTROL ────────────────────────────────────────
 *
 * `shown` is rendered next to Reset. A filter combination that returns nothing
 * looks exactly like a broken screen, and "0 shown" is the difference between
 * "there is nothing matching this" and "something is wrong". It counts what
 * came back, which is capped at 25 by the query — so it says "25" and not
 * "25 of 200", because the second number is not known here and inventing a
 * total would be worse than omitting one.
 *
 * ── IT IS NOT A PERMISSION ──────────────────────────────────────────────────
 *
 * Every value here goes into wiki_search as a REQUEST. RLS decides. This
 * component being hidden from an employee is a courtesy to the employee, not
 * a control on the data — see the route header and migration 039.
 */
function CuratorBar({
  filters,
  onChange,
  onReset,
  shown,
}: {
  filters: WikiFilters
  onChange: <K extends keyof WikiFilters>(key: K, value: WikiFilters[K]) => void
  onReset: () => void
  shown: number
}) {
  return (
    <div className="curate" role="group" aria-label="Curator filters">
      <p className="curate__head">
        <span className="curate__dot" aria-hidden="true" />
        Curating
      </p>

      <div className="curate__rows">
        <FilterRow
          label="Show"
          value={filters.state ?? 'published'}
          options={[
            ['all', 'All'],
            ['published', 'Published'],
            ['unpublished', 'Unpublished'],
          ]}
          onPick={(v) => onChange('state', v as WikiFilters['state'])}
        />
        <FilterRow
          label="Official"
          value={filters.official ?? 'any'}
          options={[
            ['any', 'Any'],
            ['official', 'Official'],
            ['unofficial', 'Not official'],
          ]}
          onPick={(v) => onChange('official', v as WikiFilters['official'])}
        />
        <FilterRow
          label="Review"
          value={filters.review ?? 'any'}
          options={[
            ['any', 'Any'],
            // Same definition as the "not reviewed in the last year" pill on
            // the rows below — app.wiki_needs_review(), one function in the
            // database, so the badge and this filter cannot disagree.
            ['needs', 'Needs review'],
          ]}
          onPick={(v) => onChange('review', v as WikiFilters['review'])}
        />
        <FilterRow
          label="Type"
          value={filters.source ?? 'any'}
          options={[
            ['any', 'Any'],
            ['wiki_answer', 'Answers'],
            ['wiki_question', 'Questions'],
          ]}
          onPick={(v) => onChange('source', v as WikiFilters['source'])}
        />
        <FilterRow
          label="Owner"
          value={filters.owner ?? 'any'}
          // "Mine" here is content you are ANSWERABLE for — answers you own,
          // questions you asked. It is deliberately narrower than the "Yours"
          // sort above, which also counts threads you voted in or replied to.
          // Two different questions; do not merge the controls.
          options={[
            ['any', 'Anyone'],
            ['mine', 'Mine'],
          ]}
          onPick={(v) => onChange('owner', v as WikiFilters['owner'])}
        />
        {/* 098. The content check's reading, applied BY THE DATABASE through
            the same app.moderation_temperature() the row pill shows — so a
            row this chip selected always wears the matching pill. The list is
            TEMPERATURES, the one the pill reads too. */}
        <FilterRow
          label="Temperature"
          value={filters.temperature ?? 'any'}
          options={[['any', 'Any'], ...TEMPERATURES.map((t): [string, string] => [t.key, t.label])]}
          onPick={(v) => onChange('temperature', v as WikiFilters['temperature'])}
        />
      </div>

      <p className="curate__foot">
        <span className="curate__count">{shown} shown</span>
        <button type="button" className="btn btn--quiet" onClick={onReset}>
          Reset filters
        </button>
      </p>
    </div>
  )
}

/**
 * `.chip chip--sm`, the same pill as the sort control above and the feed's
 * audience filter. The first cut of this bar used a joined `.segmented`
 * control; separate pills won because a segmented control implies the options
 * partition one axis with no gaps, and "Any / Official / Not official" reads
 * better as three things you can pick than as a switch with three positions.
 * More to the point, the app already had a pill and did not need a second
 * vocabulary for the same act.
 */
function FilterRow({
  label,
  value,
  options,
  onPick,
}: {
  label: string
  value: string
  options: [string, string][]
  onPick: (v: string) => void
}) {
  return (
    <div className="curate__row">
      <span className="curate__label">{label}</span>
      <div className="chips" role="group" aria-label={label}>
        {options.map(([key, text]) => (
          <button
            key={key}
            type="button"
            className={`chip chip--sm${value === key ? ' chip--on' : ''}`}
            aria-pressed={value === key}
            onClick={() => onPick(key)}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * One question in the curator's queue.
 *
 * Publishing and answering are different acts and different buttons.
 * Publishing puts the question on the board so colleagues can reply to it;
 * answering writes an official Wiki One answer. A question can be published
 * and stay open — that is the normal case, and it is why content.state and
 * content_wiki_question.status are separate axes in 034.
 *
 * The answer it writes is a DRAFT, because create_wiki_answer only writes
 * drafts. It then needs publishing like anything else.
 */
function QueueRow({
  q,
  canPublish,
  onDone,
  onError,
}: {
  q: WikiQuestion
  canPublish: boolean
  onDone: () => Promise<void>
  onError: (m: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState(q.question)
  const [body, setBody] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  async function run(fn: () => Promise<unknown>) {
    setBusy(true)
    try {
      await fn()
      await onDone()
    } catch (e) {
      onError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <li className="wiki-queue__item">
      <p className="wiki-queue__body">{q.question}</p>
      {q.detail && q.detail !== q.question && (
        <p className="wiki-queue__detail">{q.detail}</p>
      )}
      <p className="wiki-queue__meta">
        {q.askedBy ?? 'Unknown'} · {new Date(q.askedAt).toLocaleDateString()}
        {!q.isPublished && <> · not on the board yet</>}
      </p>

      <p className="wiki-result__actions">
        {canPublish && !q.isPublished && (
          <button
            className="btn btn--primary btn--sm"
            disabled={busy}
            onClick={() => void run(() => publishWikiContent(q.questionId))}
          >
            Put on the board
          </button>
        )}
        <button className="btn btn--quiet" onClick={() => setOpen(!open)}>
          {open ? 'Cancel' : 'Answer officially'}
        </button>
      </p>

      {open && (
        <div className="wiki-queue__form">
          <input
            className="composer__title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="The question, as it will be published"
          />
          <textarea
            className="composer__body"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="The official answer"
          />
          <button
            className="btn btn--primary btn--sm"
            disabled={busy || !body.trim()}
            onClick={() => void run(() => answerQuestion(q.questionId, title, body))}
          >
            Save as draft answer
          </button>
          <input
            className="composer__title"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Or say why this will not be answered"
          />
          <button
            className="btn btn--quiet"
            disabled={busy || !note.trim()}
            onClick={() => void run(() => declineQuestion(q.questionId, note))}
          >
            Decline
          </button>
        </div>
      )}
    </li>
  )
}

/**
 * One row of results, used by both lists on this screen.
 *
 * It is a component rather than a function called twice because the two lists —
 * what One cited, and what the lexical search matched — must render
 * identically. A reader has no way to tell which list a row came from, and they
 * should not need to: an official answer looks official either way. Two copies
 * of this markup would drift the first time either list gained a badge.
 */
function ResultRow({
  r,
  me,
  busy,
  openThread,
  setOpenThread,
  canCurate,
  canPublish,
  ownerControls,
  showTemperature,
  onVote,
  act,
}: {
  r: WikiResult
  me: Me
  busy: string | null
  openThread: string | null
  setOpenThread: (id: string | null) => void
  canCurate: boolean
  canPublish: boolean
  /** 031b/041's owner powers. False for a curator outside curator mode. */
  ownerControls: boolean
  /** 098. Curator mode only. */
  showTemperature: boolean
  onVote: (votes: number, iVoted: boolean) => void
  act: (id: string, fn: () => Promise<unknown>) => Promise<void>
}) {
  const lang = useHubLang()
  const w = WIKI[lang]
  // 187 (WIK-28): the edit dialog. Curator mode only — `canCurate` here is
  // already canCurate && curatorOn (098: the switch, never the permission).
  const [editing, setEditing] = useState(false)
  const mayEdit = canCurate && r.source === 'wiki_answer'
  // 112. A machine-translated answer says so above its question, and offers
  // the source — the same line, and the same state, as a translated post.
  const orig = useOriginal(r.translationStatus === 'machine', () => loadWikiOriginal(r.contentId))
  const shownQuestion = orig.showingOriginal && orig.original ? orig.original.title : r.question
  const shownAnswer = orig.showingOriginal && orig.original ? orig.original.body : r.answer
  const shownLang = orig.showingOriginal && orig.original ? orig.original.locale : undefined
  return (
    <li className="wiki-result">
      {/* 13 Oct: no vote gutter. The upvote used to sit in a column of its own
          down the left of every row; with the 44px touch floor that column
          plus its gap took a quarter of a phone's width from the answer. It
          now opens the action row, which is where VoteButton's header (12 Sep,
          "an inline caret and a number in the actions row") already said it
          lived. */}
      <div className="wiki-result__row">
        <div className="wiki-result__main">
          <TranslatedLine state={orig} />
          <p className="wiki-result__q" lang={shownLang}>
            {/* The badge is the ranking rule made visible. If it is on screen
                and the row is not at the top of the lexical list, the ORDER BY
                changed, not this badge. */}
            {r.isOfficial && (
              <span className="pill pill--star" title={w.officialTitle}>
                {w.official}
              </span>
            )}
            {r.source === 'wiki_question' && <span className="pill pill--kind">{w.question}</span>}
            {r.state === 'draft' && <span className="pill pill--draft">{w.unpublished}</span>}
            {showTemperature && <TemperaturePill temperature={r.temperature} />}
            {shownQuestion}
          </p>
          <p className="wiki-result__a" lang={shownLang}>{shownAnswer}</p>
          <p className="wiki-result__meta">
            {r.source === 'wiki_question'
              ? r.personName
                ? w.askedBy(r.personName)
                : w.asked
              : r.personName
                ? w.ownedBy(r.personName)
                : w.unowned}
            {r.replies > 0 && <> · {w.replies(r.replies)}</>}
            {/* 187, David's ruling 2: a reworded answer says so, for everyone.
                The date is when the words changed after it went out
                (content_variant.edited_at), never the last review. */}
            {r.editedAt && (
              <>
                {' · '}
                {w.updatedOn(
                  new Date(r.editedAt).toLocaleDateString(dateLocaleFor(lang), {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  }),
                )}
              </>
            )}
            {r.isFallback && <> · {w.shownIn(r.variantLocale)}</>}
            {/* Stale is shown, never used to filter. A stale answer is still
                the official answer, and hiding it sends people back to the
                2024 PDF Wiki One replaces. */}
            {r.isStale && (
              <>
                {' '}
                <span className="pill pill--stale">{w.stale}</span>
              </>
            )}
          </p>

          {/* ── THE ACTION ROW ─────────────────────────────────────────────

              Everything here is one height and nothing changes width when it
              is used. The 8 Sep version had a `btn--primary` Publish that was
              taller and wider than its neighbours, so acting on one row moved
              the controls on every other row — in a list of 26 drafts, the
              button you were aiming at is somewhere else by the time you look
              back.

              Reading order is deliberate: what everyone can do (the upvote,
              then replies), then the two state toggles, then the validation
              with its date, then the destructive action last and visually
              apart. */}
          <div className="rowactions">
            <VoteButton
              votes={r.votes}
              iVoted={r.iVoted}
              target={{ contentId: r.contentId }}
              onChange={onVote}
            />
            <button
              className="btn btn--quiet btn--row"
              onClick={() => setOpenThread(openThread === r.contentId ? null : r.contentId)}
            >
              {openThread === r.contentId
                ? w.hideReplies
                : r.replies > 0
                  ? w.repliesCount(r.replies)
                  : w.reply}
            </button>

            {/* Publication as ONE control showing the current state. 040 made
                the second half possible; before it, "Unpublish" could not
                exist because leaving `published` erased the record that the
                item went out. A POST has no equivalent and must not gain one —
                a dated utterance people read cannot be un-said. */}
            {canPublish && (
              <RowToggle
                label="Publication"
                value={r.state === 'published' ? 'published' : 'draft'}
                busy={busy === r.contentId}
                options={[
                  { key: 'draft', text: 'Draft', title: 'Take this off the board. Publishing again puts it back.' },
                  { key: 'published', text: 'Published', title: 'Put this on the board for everyone.' },
                ]}
                onPick={(k) =>
                  void act(r.contentId, () =>
                    k === 'published'
                      ? publishWikiContent(r.contentId)
                      : unpublishWikiContent(r.contentId),
                  )
                }
              />
            )}

            {canCurate && (
              <RowToggle
                label="Official"
                value={r.isOfficial ? 'official' : 'unofficial'}
                busy={busy === r.contentId}
                options={[
                  { key: 'unofficial', text: 'Unofficial' },
                  { key: 'official', text: 'Official', title: 'This is right — rely on it. Official always outranks everything else on the board.' },
                ]}
                onPick={(k) =>
                  void act(r.contentId, () =>
                    r.source === 'wiki_question'
                      ? curateQuestion(r.contentId, k === 'official' ? 'official' : 'unofficial')
                      : curateAnswer(r.contentId, k === 'official' ? 'star' : 'unstar'),
                  )
                }
              />
            )}

            {/* 187 (WIK-28, David 12 Oct): changing an answer's WORDS is a
                curator's act. The owner keeps "Still true" below and nothing
                more — the database refuses them too, so this is presentation. */}
            {mayEdit && (
              <button
                type="button"
                className="btn btn--quiet btn--row"
                disabled={busy === r.contentId}
                title={w.editTitle}
                onClick={() => setEditing(true)}
              >
                {w.edit}
              </button>
            )}

            {/* 031b: an answer's OWNER may confirm it even without
                wiki.curate — that is what owning it means. A question has no
                owner, so the owner half is answers only. */}
            {(canCurate ||
              (ownerControls && r.source === 'wiki_answer' && r.ownerPersonId === me.personId)) && (
              <StillTrue
                reviewedAt={r.lastReviewedAt}
                isStale={r.isStale}
                busy={busy === r.contentId}
                onConfirm={() =>
                  void act(r.contentId, () =>
                    r.source === 'wiki_question'
                      ? curateQuestion(r.contentId, 'review')
                      : curateAnswer(r.contentId, 'review'),
                  )
                }
              />
            )}

            {/* 041. THE LABEL SAYS WHICH DELETE THIS IS, from
                firstPublishedAt and never from `state` — after 040 an item can
                sit at state 'draft' having been published for a fortnight, and
                four live rows were in exactly that position on 8 Sep.

                Never published, so nobody saw it → it really goes, and the
                author may do it themselves. Ever published → a soft delete
                that keeps the record, and only a curator. Confirm on the
                permanent one only: the reversible neighbours must stay cheap. */}
            {(canCurate || (ownerControls && r.ownerPersonId === me.personId)) && (
              <button
                className="btn btn--danger btn--row rowactions__end"
                disabled={busy === r.contentId || (!canCurate && r.firstPublishedAt !== null)}
                title={
                  r.firstPublishedAt === null
                    ? w.deleteTitle
                    : w.removeTitle
                }
                onClick={() => {
                  if (
                    r.firstPublishedAt === null &&
                    !window.confirm(w.confirmDelete)
                  ) {
                    return
                  }
                  void act(r.contentId, () => deleteWikiContent(r.contentId))
                }}
              >
                {r.firstPublishedAt === null ? w.delete : w.remove}
              </button>
            )}
          </div>

          {mayEdit && (
            <EditAnswerDialog
              contentId={r.contentId}
              open={editing}
              onClose={() => setEditing(false)}
              // The write happened in the dialog; act() is the one re-read
              // (never a splice — 028c), so it is handed a no-op.
              onSaved={() => void act(r.contentId, async () => undefined)}
            />
          )}

          {openThread === r.contentId && <Thread contentId={r.contentId} me={me} />}
        </div>
      </div>
    </li>
  )
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}
